import { Item } from '../item/item.types';
import { Schedule } from '../schedule/schedule.types';
import { Vehicle } from '../vehicle/vehicle.types';
import { VehicleWorkshop } from '../vehicle-workshop/vehicle-workshop.types';

/**
 * Texts a report version printed in sections 1-2, frozen when the version was
 * generated. Versions written before the snapshot existed do not carry it:
 * consumers fall back to the current event fields.
 */
export interface ReportTexts {
  description?: string;
  clientRequestNote?: string;
  technicalDiagnosis?: string;
  technicalRecommendation?: string;
  /** Section 4 of the preliminary report (LION-986). Absent on older snapshots. */
  conclusion?: string;
}

/**
 * One generated version of a technical report PDF (preliminary or final).
 * Written by the server — the preliminary one on every budget submit, the
 * final one when the event reaches COMPLETED; the UI never uploads or deletes
 * it. `GET events/:id` replaces `key` with its signed URL in place, like every
 * other document field — there is no separate `url`.
 */
export interface ReportVersion {
  key: string;
  version: number;
  generatedAt: string;
  generatedBy: string;
  eventStatus: string;
  texts?: ReportTexts;
}

export type PreliminaryReportVersion = ReportVersion;
export type FinalReportVersion = ReportVersion;

export interface EventDocuments {
  budget?: string; // presupuesto
  workOrder?: string; // ot
  invoice?: string; // factura
  deliveryNotes?: string; // remitos
  reception?: string[]; // preliminar - array de imágenes
  delivery?: string[]; // diagnóstico finalizado - array de imágenes
  others?: string[]; // otros documentos - array
  /** Preliminary technical report history, oldest first (server-written). */
  preliminaryReports?: PreliminaryReportVersion[];
  /** Final report ("Diagnóstico finalizado") history, oldest first (server-written). */
  finalReports?: FinalReportVersion[];
}

/**
 * Ítem guardado que después se eliminó del presupuesto (`event.removedItems`).
 * Ya no existe en `items`: no suma ni se decide. Sin precio, a propósito.
 */
export interface EventRemovedItem {
  itemId: string;
  name: string;
  quantity: number;
  /** El conjunto al que pertenecía (vigente o eliminado); la tabla lo muestra adentro. */
  groupId?: string | null;
  removedAt: string;
  removedBy?: string;
}

/** Conjunto guardado que después se eliminó (`event.removedGroups`). Sus ítems quedaron sin conjunto. */
export interface EventRemovedGroup {
  groupId: string;
  name?: string;
  hours: number;
  removedAt: string;
  removedBy?: string;
}

export interface Event {
  _id: string;
  number: number;
  title?: string;
  technicalDiagnosis?: string;
  /** Preliminary report, section 1: optional workshop note on the client request (`description`). */
  clientRequestNote?: string;
  /** Preliminary report, section 2: technical recommendation, next to `technicalDiagnosis`. */
  technicalRecommendation?: string;
  /** Preliminary report, section 4. */
  conclusion?: string;
  /** Final report, section 4 ("Trabajos realizados"). Required to reach COMPLETED. */
  workPerformed?: string;
  /** Final report, section 5 ("Conclusión"); not the preliminary `conclusion`. Required to reach COMPLETED. */
  finalConclusion?: string;
  description: string;
  startDate: string;
  endDate: string;
  status: EVENT_STATUS;
  vehicle: Vehicle;
  items: Item[];
  price: number;
  audits: EventAudit[];
  approvals: EventApproval[];
  documents?: EventDocuments;
  comment?: string;
  rejectionComment?: string;
  schedule?: Schedule;
  workshop?: VehicleWorkshop;
  vehicleReceivedAt?: string;
  workshopStartDate?: string;
  workshopEndDate?: string;
  vehicleExitDate?: string;
  hours?: number;
  invoiceDate?: string;
  invoicedAt?: string;
  serviceNumber?: number;
  deletedAt?: string;
  hasRejectedItems?: boolean;
  /**
   * LION-950 — alcance de la recotización pedida, persistido por el back al
   * entrar en `PARTIAL_REJECTION` y limpiado al volver a `BUDGETED`.
   *
   * Es la decisión que se tomó, NO un recuento vivo de ítems: después de pedir
   * la recotización el taller edita y reenvía ítems, así que recalcularlo en el
   * front daría vuelta el label solo (un evento decidido como total pasaría a
   * leerse parcial en cuanto el taller toque un ítem).
   *
   * Ausente en eventos legacy y mientras el API no esté deployado → la UI
   * muestra el neutro `REQUOTE_UNKNOWN_LABEL`.
   */
  requoteScope?: REQUOTE_SCOPE;
  /** LION-644 — grupo de cotización: eventos que compiten por el mismo trabajo. */
  quoteGroupId?: string;
  /** LION-644 — motivo por el que se pidió un presupuesto a otro establecimiento. */
  externalQuoteReason?: string;
  /**
   * LION-986 — solo en el evento del taller nuevo de una cotización externa:
   * motivo + informe técnico que compartió el taller original (sin ítems ni
   * precios, y sin identificar al taller).
   */
  externalQuoteContext?: ExternalQuoteContext;
  /** event-budget-item-groups: grupos ("conjuntos") del presupuesto, expuestos por `GET /events/:id`. */
  groups?: ItemGroupView[];
  /** Ítems que llegaron a guardarse y después se eliminaron: la tabla los muestra como «Eliminado». */
  removedItems?: EventRemovedItem[];
  /** Conjuntos guardados que después se eliminaron: la tabla los muestra como «Eliminado». */
  removedGroups?: EventRemovedGroup[];
  /**
   * event-budget-item-groups: total de horas hombre derivado (suma de horas de
   * grupos no rechazados + ítems sin grupo no rechazados). Calculado por el
   * back en cada lectura, nunca aceptado como input directo.
   */
  laborHoursTotal?: number;
}

/**
 * event-budget-item-groups: estado derivado de un grupo a partir del status de
 * sus ítems miembro (design.md §5 / spec `event-item-group-approval`).
 */
export type ItemGroupDerivedStatus = 'pending' | 'accepted' | 'rejected' | 'partial';

/** event-budget-item-groups: forma de un grupo tal como la expone el read model del evento. */
export interface ItemGroupView {
  _id: string;
  name?: string;
  hours: number;
  /** Derivado on-read; ausente antes de la primera decisión de grupo. */
  derivedStatus?: ItemGroupDerivedStatus;
  /** true = la cascada de la última decisión quedó incompleta (design.md §4, "No re-apply on read"). */
  cascadePending?: boolean;
}

export enum APPROVAL_LEVEL {
  PVP = 'PVP',
  COMPANY = 'COMPANY'
}

export enum APPROVAL_METHOD {
  WEB = 'web',
  EMAIL = 'email'
}

/**
 * LION-950 — alcance de una recotización. Espeja `RequoteScope` del API
 * (PVP-Main-Api, `events` module). Ver `Event.requoteScope`.
 */
export enum REQUOTE_SCOPE {
  PARTIAL = 'PARTIAL',
  TOTAL = 'TOTAL'
}

/**
 * NOTA (LION-950): `PARTIAL_REJECTION` es un misnomer heredado. Ya no significa
 * "rechazo parcial" sino "en recotización": el presupuesto vuelve al taller
 * para que corrija ítems, y el evento sigue vivo con el mismo número. El
 * rechazo real es `REJECTED` (el evento se cae y hay que generar uno nuevo).
 * No se renombra el valor del enum porque implicaría migrar la colección
 * `events` completa más las transiciones, el dashboard y los reportes; el
 * cambio es de LABEL — ver `requoteLabel()` en `event-status.meta.ts`.
 */
export enum EVENT_STATUS {
  OPEN = 'OPEN',
  RECEIVED = 'RECEIVED',
  IN_PROGRESS = 'IN_PROGRESS',
  BUDGETED = 'BUDGETED',
  PARTIAL_APPROVAL = 'PARTIAL_APPROVAL',
  APPROVED = 'APPROVED',
  COMPLETED = 'COMPLETED',
  PARTIAL_REJECTION = 'PARTIAL_REJECTION',
  REJECTED = 'REJECTED',
  DELIVERED = 'DELIVERED'
}

interface EventAudit {
  status: string;
  date: Date;
}

/**
 * Usuario embebido en una EventApproval cuando el back popula el campo.
 * Si la orden está cargada sin populate, `user` viene como string id.
 */
export interface EventApprovalUser {
  _id: string;
  firstName: string;
  lastName: string;
  email?: string;
  role?: string;
}

export interface EventApproval {
  user: string | EventApprovalUser;
  approvedAt: string;
  /** Nivel del firmante en el flujo DUAL. Phase B en adelante. */
  level?: APPROVAL_LEVEL;
  /** Método de firma: web o link de email. */
  method?: APPROVAL_METHOD;
}

/**
 * Progreso del flujo de aprobación. Phase B agrega esta forma; el back
 * devuelve `null` cuando la orden no está en BUDGETED.
 */
export interface ApprovalProgress {
  mode: 'SINGLE' | 'DUAL_PVP_COMPANY';
  pvpSigned: boolean;
  companySigned: boolean;
  /** Siguiente nivel esperado para cerrar el flujo. null = lista para APPROVED. */
  nextExpectedLevel: APPROVAL_LEVEL | null;
  message: string;
}

export interface EventRejection {
  user: { _id: string; firstName: string; lastName: string; role?: string } | null;
  rejectedAt: string | null;
  method: string | null;
  comment: string | null;
}

export interface ApprovalStatusResponse {
  status: EVENT_STATUS;
  approvals: EventApproval[];
  rejection: EventRejection | null;
  progress: ApprovalProgress | null;
  canCurrentUserApprove: boolean;
  canCurrentUserReject: boolean;
  reasonIfBlocked: string | null;
  isBlockedBySequentialOrder: boolean;
  isBlockedByValidationRules: boolean;
}

/**
 * LION-986 — what the workshop asked to quote a job another workshop already
 * diagnosed gets to see. The server copies the texts from the latest
 * PRELIMINARY report snapshot of the source event (never the drafts), and
 * never items, prices, photos or the source workshop's name.
 */
export interface ExternalQuoteContext {
  reason?: string;
  clientRequestNote?: string;
  technicalDiagnosis?: string;
  technicalRecommendation?: string;
  conclusion?: string;
  /** ISO date of the source report version. */
  sourceReportGeneratedAt?: string;
  sourceReportVersion?: number;
  /** @deprecated Event title of contexts written before LION-986; no longer written, still rendered. */
  title?: string;
}
